import type {
  Exigences,
  ModeleEtape,
  Preuve,
  PreuveSav,
  Relance,
  StatutEtape,
  TypePreuve,
} from '@/shared/donnees/types';
import { DOCUMENTS_DE_DEPART, MODELE_PAR_DEFAUT } from '@/shared/referentiel/chantier';
import { aujourdhui } from '@/shared/referentiel/conformite';
import type { ChantierDemo, EtapeDemo, TicketDemo } from './magasin';

function jour(decalage: number): string {
  const [a, m, j] = aujourdhui().split('-').map(Number);
  return new Date(Date.UTC(a ?? 2026, (m ?? 1) - 1, (j ?? 1) + decalage))
    .toISOString()
    .slice(0, 10);
}

/** Un horodatage à 17 h, heure de Paris, `decalage` jours plus tôt ou plus tard. */
function moment(decalage: number, heure = 17): string {
  return `${jour(decalage)}T${String(heure - 2).padStart(2, '0')}:05:00.000Z`;
}

interface PlanEtape {
  statut: StatutEtape;
  partenaire?: string;
  debut?: number;
  fin?: number;
  validee?: number;
  validePar?: string;
  /** Preuves déposées, en plus de celles qu'exige une étape envoyée ou validée. */
  photos?: number;
  motif?: string;
  reserve?: { texte: string; jour: number; par: string };
}

/** Les étapes de la chaîne de départ, par leur nom court dans les plans ci-dessous. */
const CLES = [
  'visite',
  'panneaux',
  'solaire',
  'batiment',
  'cellule',
  'sechage',
  'reception',
] as const;
type Cle = (typeof CLES)[number];
type Plan = Partial<Record<Cle, PlanEtape>>;

/** Le modèle de départ, avec des identifiants stables pour l'accès libre. */
/** Les attentes de départ, par position dans la chaîne : on n'installe pas ce qu'on n'a pas reçu. */
const ATTENTES: Record<number, number> = { 1: 0, 2: 1, 3: 0, 4: 0, 5: 4 };

export function modeleDeDepart(): ModeleEtape[] {
  return MODELE_PAR_DEFAUT.map((m, i) => {
    const attendue = ATTENTES[i];
    return {
      ...m,
      id: `me-${CLES[i] ?? String(i)}`,
      exigences: { photosMin: m.exigences.photosMin, documents: [...m.exigences.documents] },
      attend: attendue === undefined ? null : `me-${CLES[attendue] ?? String(attendue)}`,
    };
  });
}

/**
 * Les chantiers de la graine datent d'avant la visite technique : un chantier
 * déjà commencé a eu sa visite, validée et faite par le partenaire de sa
 * première étape. Un chantier qui n'a rien commencé l'attend encore.
 */
function visiteDeLaGraine(plan: Plan, cle: string): PlanEtape {
  const etapes: PlanEtape[] = Object.values(plan);
  const commence = etapes.some((x) => x.statut !== 'a-planifier');
  if (cle !== 'visite' || !commence) return { statut: 'a-planifier' };
  const premiere = etapes.find((x) => x.partenaire);
  return {
    statut: 'validee',
    ...(premiere?.partenaire ? { partenaire: premiere.partenaire } : {}),
    debut: -45,
    fin: -45,
    validee: -44,
  };
}

function construire(chantier: ChantierDemo, plan: Plan) {
  const etapes: EtapeDemo[] = [];
  const preuves: Preuve[] = [];
  MODELE_PAR_DEFAUT.forEach((modele, i) => {
    const cle = CLES[i] ?? String(i);
    const p = plan[cle as Cle] ?? visiteDeLaGraine(plan, cle);
    const exigences: Exigences = {
      photosMin: modele.exigences.photosMin,
      documents: [...modele.exigences.documents],
    };
    const etapeId = `${chantier.id}-${cle}`;
    const attendue = ATTENTES[i];
    const complete = p.statut === 'validee' || p.statut === 'a-valider';
    etapes.push({
      id: etapeId,
      attend:
        attendue === undefined ? null : `${chantier.id}-${CLES[attendue] ?? String(attendue)}`,
      chantierId: chantier.id,
      ordre: modele.ordre,
      libelle: modele.libelle,
      metier: modele.metier,
      icone: modele.icone,
      finale: modele.finale,
      partenaireId: p.partenaire ?? null,
      debutPrevu: p.debut === undefined ? null : jour(p.debut),
      finPrevue: p.fin === undefined ? null : jour(p.fin),
      statut: p.statut,
      demarreeLe:
        p.statut === 'en-cours' || complete || p.statut === 'rouverte'
          ? moment(p.debut ?? -1, 8)
          : null,
      soumiseLe: complete ? moment(p.validee ?? p.fin ?? 0, 15) : null,
      valideeLe: p.statut === 'validee' ? moment(p.validee ?? p.fin ?? 0) : null,
      valideePar: p.statut === 'validee' ? 'Farid Beghache' : null,
      motifReouverture: p.motif ?? null,
      reserve: p.reserve
        ? {
            texte: p.reserve.texte,
            signaleeLe: moment(p.reserve.jour, 11),
            signaleePar: p.reserve.par,
          }
        : null,
      exigences,
    });

    const nbPhotos =
      (complete || p.statut === 'rouverte' ? exigences.photosMin : 0) + (p.photos ?? 0);
    const quand = moment(p.validee ?? p.fin ?? p.debut ?? 0, 16);
    const ajout = (type: TypePreuve, nom: string, n: number) => {
      const preuveId = `${etapeId}-${type}-${n}`;
      preuves.push({
        id: preuveId,
        etapeId,
        type,
        nom,
        taille: 1_200_000 + n * 37_000,
        typeMime: nom.endsWith('.pdf') ? 'application/pdf' : 'image/jpeg',
        chemin: `chantiers/${chantier.id}/${etapeId}/${preuveId}-${nom}`,
        ajouteLe: quand,
        ajoutePar: null,
      });
    };
    for (let n = 1; n <= nbPhotos; n++) ajout('photo', `${cle}-${n}.jpg`, n);
    if (complete) {
      exigences.documents.forEach((doc) => {
        const nom = DOCUMENTS_DE_DEPART.find((d) => d.id === doc)?.libelle ?? doc;
        ajout(doc, `${nom}.pdf`, 1);
      });
    }
  });
  return { etapes, preuves };
}

/** Les chantiers des maquettes, avec des dates relatives à aujourd'hui. */
export function graineChantiers() {
  const chantiers: ChantierDemo[] = [
    {
      id: 'ch-bousquet',
      reference: 'CH-2026-014',
      entrepriseId: 'e-bousquet',
      siteId: 's-hangar-nord',
      titre: 'Séchoir solaire 180 m²',
      batiment: 'existant',
      notes: '',
      creeLe: moment(-20, 10),
    },
    {
      id: 'ch-marre',
      reference: 'CH-2026-015',
      entrepriseId: 'e-marre',
      siteId: 's-marre',
      titre: 'Séchoir à noix 120 m²',
      batiment: 'construction',
      notes: 'Dalle coulée par le client avant l’arrivée de la charpente.',
      creeLe: moment(-12, 10),
    },
    {
      id: 'ch-saint-joseph',
      reference: 'CH-2026-011',
      entrepriseId: 'e-saint-joseph',
      siteId: 's-remparts',
      titre: 'Séchoir à bois 240 m²',
      batiment: 'existant',
      notes: 'Matériel arrivé par conteneur au Port.',
      creeLe: moment(-45, 10),
    },
    {
      id: 'ch-pujo',
      reference: 'CH-2026-016',
      entrepriseId: 'e-pujo',
      siteId: 's-lesponne',
      titre: 'Séchoir à foin 90 m²',
      batiment: 'existant',
      notes: '',
      creeLe: moment(-6, 10),
    },
    /* Réceptionné il y a plus de 30 jours : une archive, que les listes ne
       chargent plus par défaut. */
    {
      id: 'ch-borie',
      reference: 'CH-2026-004',
      entrepriseId: 'e-bousquet',
      siteId: 's-borie',
      titre: 'Séchoir à prunes 150 m²',
      batiment: 'existant',
      notes: '',
      creeLe: moment(-150, 10),
    },
  ];

  const plans: Record<string, Plan> = {
    'ch-bousquet': {
      panneaux: {
        statut: 'validee',
        partenaire: 'p-riviere',
        debut: -9,
        fin: -9,
        validePar: 'L. Rivière',
      },
      solaire: {
        statut: 'a-valider',
        partenaire: 'p-solaire-aveyron',
        debut: -6,
        fin: -4,
        validee: 0,
        photos: 9,
      },
      batiment: { statut: 'en-cours', partenaire: 'p-batisud', debut: -5, fin: 1, photos: 3 },
      sechage: { statut: 'planifiee', partenaire: 'p-aero-sechage', debut: 6, fin: 8 },
    },
    'ch-marre': {
      solaire: { statut: 'planifiee', partenaire: 'p-solaire-aveyron', debut: 11, fin: 13 },
      batiment: { statut: 'planifiee', partenaire: 'p-batisud', debut: 1, fin: 5 },
    },
    'ch-saint-joseph': {
      panneaux: {
        statut: 'validee',
        partenaire: 'p-energie-pei',
        debut: -30,
        fin: -30,
        validePar: 'S. Payet',
      },
      solaire: {
        statut: 'validee',
        partenaire: 'p-energie-pei',
        debut: -20,
        fin: -16,
        validePar: 'S. Payet',
      },
      batiment: {
        statut: 'validee',
        partenaire: 'p-energie-pei',
        debut: -15,
        fin: -12,
        validePar: 'S. Payet',
      },
      sechage: {
        statut: 'en-cours',
        partenaire: 'p-energie-pei',
        debut: -3,
        fin: 2,
        photos: 1,
        reserve: {
          texte:
            'Ventilateur livré avec une pale fêlée : remplacement demandé au fournisseur, montage du reste poursuivi.',
          jour: -1,
          par: 'S. Payet',
        },
      },
    },
    'ch-pujo': {
      panneaux: { statut: 'planifiee', partenaire: 'p-riviere', debut: -3, fin: -2 },
    },
    'ch-borie': {
      panneaux: { statut: 'validee', partenaire: 'p-riviere', debut: -130, fin: -130 },
      solaire: { statut: 'validee', partenaire: 'p-solaire-aveyron', debut: -120, fin: -116 },
      batiment: { statut: 'validee', partenaire: 'p-batisud', debut: -110, fin: -104 },
      cellule: { statut: 'validee', partenaire: 'p-riviere', debut: -100, fin: -100 },
      sechage: { statut: 'validee', partenaire: 'p-aero-sechage', debut: -95, fin: -90 },
      reception: { statut: 'validee', partenaire: 'p-batisud', debut: -75, fin: -75 },
    },
  };

  const etapes: EtapeDemo[] = [];
  const preuves: Preuve[] = [];
  for (const chantier of chantiers) {
    const construit = construire(chantier, plans[chantier.id] ?? {});
    etapes.push(...construit.etapes);
    preuves.push(...construit.preuves);
  }

  return {
    modele: modeleDeDepart(),
    typesDocument: DOCUMENTS_DE_DEPART.map((d) => ({ ...d })),
    chantiers,
    etapes,
    preuves,
    ...graineSav(),
  };
}

/** Trois tickets SAV : un urgent à planifier, un planifié, un résolu qui attend sa clôture. */
export function graineSav(): {
  tickets: TicketDemo[];
  preuvesSav: PreuveSav[];
  relances: Relance[];
} {
  const vierge = {
    partenaireId: null,
    debutPrevu: null,
    finPrevue: null,
    demarreLe: null,
    resoluLe: null,
    resoluPar: null,
    rapport: '',
    closLe: null,
    closPar: null,
    motifReouverture: null,
  };
  const tickets: TicketDemo[] = [
    {
      ...vierge,
      id: 'sav-onduleur',
      reference: 'SAV-2026-001',
      chantierId: 'ch-saint-joseph',
      objet: 'Onduleur en défaut',
      description:
        'M. Hoarau appelle : voyant rouge sur l’onduleur depuis ce matin, ventilation arrêtée. Bois en cours de séchage.',
      priorite: 'urgente',
      statut: 'a-planifier',
      ouvertLe: moment(0, 9),
      ouvertPar: 'Bureau VLA',
    },
    {
      ...vierge,
      id: 'sav-gaine',
      reference: 'SAV-2026-002',
      chantierId: 'ch-bousquet',
      objet: 'Fuite d’air sur une gaine',
      description: 'Sifflement côté pignon est, à vérifier au passage de l’équipe séchage.',
      priorite: 'normale',
      statut: 'planifie',
      partenaireId: 'p-aero-sechage',
      debutPrevu: jour(3),
      finPrevue: jour(3),
      ouvertLe: moment(-2, 14),
      ouvertPar: 'Bureau VLA',
    },
    {
      ...vierge,
      id: 'sav-sonde',
      reference: 'SAV-2026-003',
      chantierId: 'ch-saint-joseph',
      objet: 'Sonde de température muette',
      description:
        'L’affichage de la cellule reste à « -- » depuis l’orage de la semaine dernière.',
      priorite: 'normale',
      statut: 'resolu',
      partenaireId: 'p-energie-pei',
      debutPrevu: jour(-1),
      finPrevue: jour(-1),
      ouvertLe: moment(-6, 10),
      ouvertPar: 'Bureau VLA',
      demarreLe: moment(-1, 9),
      resoluLe: moment(-1, 15),
      resoluPar: 'S. Payet',
      rapport:
        'Connecteur de la sonde oxydé après l’orage. Connecteur remplacé, sonde testée : 41 °C affichés, cohérent avec le thermomètre de contrôle.',
    },
  ];
  const preuvesSav: PreuveSav[] = [
    {
      id: 'sav-sonde-photo-1',
      ticketId: 'sav-sonde',
      type: 'photo',
      nom: 'sonde-reparee.jpg',
      taille: 1_150_000,
      typeMime: 'image/jpeg',
      chemin: 'sav/sav-sonde/sav-sonde-photo-1-sonde-reparee.jpg',
      ajouteLe: moment(-1, 15),
      ajoutePar: null,
    },
  ];
  const relances: Relance[] = [
    {
      id: 'rel-batisud-urssaf',
      partenaireId: 'p-batisud',
      sujet: 'conformite:urssaf',
      envoyeeLe: moment(-3, 10),
      envoyeePar: 'Bureau VLA',
    },
  ];
  return { tickets, preuvesSav, relances };
}
